import React, { useEffect, useMemo, useState } from "react";
import { getAuth } from "firebase/auth";
import { db } from "../firebase";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import { FiCalendar, FiCopy, FiDollarSign, FiEye, FiFile, FiSearch, FiUsers } from "react-icons/fi";

const UI_CURRENCY = "LKR";
const ELIGIBLE_PAYOUT_SOURCES = new Set([
  "course_sale_affiliate",
  "product_sale_affiliate",
  "service_sale_affiliate",
]);

function money(value) {
  return `${UI_CURRENCY} ${Number(value || 0).toLocaleString("en-LK", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function shortId(value = "") {
  return value ? `${value.slice(0, 6)}...${value.slice(-4)}` : "Unknown";
}

function formatDate(value) {
  const date = value?.toDate?.() || value;
  const resolved = date instanceof Date ? date : date ? new Date(date) : null;
  return resolved ? resolved.toLocaleString() : "";
}

function monthKeyFromValue(value) {
  const source = value?.toDate?.() || value || new Date();
  const year = source.getFullYear();
  const month = String(source.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

function monthLabel(monthKey = "") {
  const [year, month] = String(monthKey || "").split("-").map(Number);
  if (!year || !month) return monthKey || "Unknown month";
  return new Date(year, month - 1, 1).toLocaleString("en-LK", {
    month: "long",
    year: "numeric",
  });
}

function titleOf(order = {}) {
  return (
    order.itemTitle ||
    order.courseTitle ||
    order.product?.title ||
    order.serviceTitle ||
    "Untitled item"
  );
}

function buyerLabel(order = {}) {
  return (
    order.affiliateLabel ||
    order.buyer?.name ||
    order.buyerName ||
    order.buyer?.email ||
    order.buyerEmail ||
    order.buyer?.phone ||
    order.buyerPhone ||
    order.userId ||
    "Affiliate"
  );
}

function slipKindOf(url = "") {
  return String(url || "").toLowerCase().includes(".pdf") ? "pdf" : "image";
}

function statusTone(status = "") {
  const value = String(status || "").toLowerCase();
  if (value === "confirmed") return "bg-green-100 text-green-700";
  if (value === "disputed") return "bg-rose-100 text-rose-700";
  if (value === "submitted") return "bg-amber-100 text-amber-800";
  return "bg-blue-100 text-blue-700";
}

function isAffiliatePayout(order = {}) {
  const affiliateUid = order.userId || null;
  const instructorUid = order.instructorUid || null;
  const commission = Number(order.amount || 0);
  return (
    Boolean(instructorUid) &&
    Boolean(affiliateUid) &&
    commission > 0 &&
    ELIGIBLE_PAYOUT_SOURCES.has(String(order.sourceType || ""))
  );
}


async function notifyUserDoc(pathSegments, payload) {
  await addDoc(collection(db, ...pathSegments), {
    read: false,
    ...payload,
    createdAt: serverTimestamp(),
  });
}

function Modal({ open, onClose, children, max = "max-w-4xl" }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className={`w-full ${max} rounded-3xl border bg-white shadow-2xl overflow-hidden`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

export default function AffiliateSettlementsAdmin() {
  const [payouts, setPayouts] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [userMap, setUserMap] = useState({});
  const [affiliateMap, setAffiliateMap] = useState({});
  const [unassignedPool, setUnassignedPool] = useState(0);
  const [monthFilter, setMonthFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [slipViewer, setSlipViewer] = useState({
    open: false,
    url: "",
    kind: "image",
    title: "",
  });
  const [ordersViewer, setOrdersViewer] = useState({
    open: false,
    row: null,
  });
  const [reviewState, setReviewState] = useState({
    open: false,
    row: null,
    status: "submitted",
    note: "",
    busy: false,
  });

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "payouts"),
      (snap) => {
        setPayouts(
          snap.docs
            .map((entry) => ({ id: entry.id, ...entry.data() }))
            .filter((entry) => isAffiliatePayout(entry)),
        );
      },
      (error) => console.error("affiliate payouts stream failed", error),
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "affiliate_settlements"),
      (snap) => {
        setSettlements(snap.docs.map((entry) => ({ id: entry.id, ...entry.data() })));
      },
      (error) => console.error("affiliate settlements stream failed", error),
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "pools", "affiliate_unassigned"), (snap) => {
      setUnassignedPool(Number(snap.data()?.balance || 0));
    });
    return () => unsub();
  }, []);

  const identityRows = useMemo(() => {
    const instructorIds = new Set();
    const affiliateIds = new Set();
    settlements.forEach((row) => {
      if (row.instructorUid) instructorIds.add(String(row.instructorUid));
      if (row.affiliateUid) affiliateIds.add(String(row.affiliateUid));
    });
    payouts.forEach((row) => {
      if (!isAffiliatePayout(row)) return;
      const instructorUid = row.instructorUid;
      const affiliateUid = row.userId;
      if (instructorUid) instructorIds.add(String(instructorUid));
      if (affiliateUid) affiliateIds.add(String(affiliateUid));
    });
    return {
      instructorIds: Array.from(instructorIds),
      affiliateIds: Array.from(affiliateIds),
    };
  }, [payouts, settlements]);

  useEffect(() => {
    const missing = identityRows.instructorIds.filter((uid) => !userMap[uid]);
    if (!missing.length) return;
    let alive = true;
    (async () => {
      const entries = await Promise.all(
        missing.map(async (uid) => {
          try {
            const [storeSnap, userSnap] = await Promise.all([
              getDoc(doc(db, "stores", uid)).catch(() => null),
              getDoc(doc(db, "users", uid)).catch(() => null),
            ]);
            const storeData = storeSnap?.exists?.() ? storeSnap.data() || {} : {};
            const userData = userSnap?.exists?.() ? userSnap.data() || {} : {};
            return [
              uid,
              {
                name:
                  storeData.displayName ||
                  userData.displayName ||
                  userData.fullName ||
                  userData.name ||
                  storeData.slug ||
                  uid,
                email: userData.email || "",
                slug: storeData.slug || "",
              },
            ];
          } catch (error) {
            console.error("failed to load instructor profile", uid, error);
            return [uid, { name: uid, email: "", slug: "" }];
          }
        }),
      );
      if (!alive) return;
      setUserMap((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
    })();
    return () => {
      alive = false;
    };
  }, [identityRows.instructorIds, userMap]);

  useEffect(() => {
    const missing = identityRows.affiliateIds.filter((uid) => !affiliateMap[uid]);
    if (!missing.length) return;
    let alive = true;
    (async () => {
      const entries = await Promise.all(
        missing.map(async (uid) => {
          try {
            const [refSnap, userSnap] = await Promise.all([
              getDoc(doc(db, "referral_users", uid)).catch(() => null),
              getDoc(doc(db, "users", uid)).catch(() => null),
            ]);
            const refData = refSnap?.exists?.() ? refSnap.data() || {} : {};
            const userData = userSnap?.exists?.() ? userSnap.data() || {} : {};
            return [
              uid,
              {
                name:
                  refData.fullName ||
                  refData.displayName ||
                  userData.displayName ||
                  userData.fullName ||
                  refData.email ||
                  userData.email ||
                  uid,
                email: refData.email || userData.email || "",
                referralCode: refData.referralCode || "",
              },
            ];
          } catch (error) {
            console.error("failed to load affiliate profile", uid, error);
            return [uid, { name: uid, email: "", referralCode: "" }];
          }
        }),
      );
      if (!alive) return;
      setAffiliateMap((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
    })();
    return () => {
      alive = false;
    };
  }, [affiliateMap, identityRows.affiliateIds]);

  const rows = useMemo(() => {
    const grouped = new Map();

    for (const settlement of settlements) {
      const key = `${settlement.monthKey}__${settlement.instructorUid}__${settlement.affiliateUid}`;
      grouped.set(key, {
        id: settlement.id,
        monthKey: settlement.monthKey,
        instructorUid: settlement.instructorUid,
        affiliateUid: settlement.affiliateUid,
        totalCommission: Number(settlement.totalCommission || 0),
        orderCount: Number(settlement.orderCount || 0),
        orderIds: Array.isArray(settlement.orderIds) ? settlement.orderIds : [],
        orders: [],
        status: settlement.status || "submitted",
        settlement,
      });
    }

    for (const payout of payouts) {
      if (!isAffiliatePayout(payout)) continue;
      const instructorUid = payout.instructorUid || null;
      const affiliateUid = payout.userId || null;
      const monthKey = payout.monthKey || monthKeyFromValue(payout.createdAt);
      const key = `${monthKey}__${instructorUid}__${affiliateUid}`;
      const existing = grouped.get(key) || {
        id: `${instructorUid}_${affiliateUid}_${monthKey}`,
        monthKey,
        instructorUid,
        affiliateUid,
        totalCommission: 0,
        orderCount: 0,
        orderIds: [],
        orders: [],
        status: "ready",
        settlement: null,
        payoutIds: [],
      };
      if (!existing.payoutIds.includes(payout.id)) {
        existing.payoutIds.push(payout.id);
        existing.totalCommission += Number(payout.amount || 0);
      }
      if (payout.orderId && !existing.orderIds.includes(payout.orderId)) {
        existing.orderIds.push(payout.orderId);
        existing.orderCount += 1;
      }
      existing.orders.push({
        id: payout.id,
        orderId: payout.orderId || "",
        itemTitle: payout.itemTitle || "",
        amount: Number(payout.amount || 0),
        level: payout.level || 0,
        createdAt: payout.createdAt || null,
        affiliateLabel:
          payout.affiliateLabel ||
          payout.affiliateName ||
          payout.userId ||
          "Affiliate",
        userId: payout.userId || "",
      });
      grouped.set(key, existing);
    }

    return Array.from(grouped.values())
      .map((row) => {
        const instructorProfile = userMap[row.instructorUid] || {};
        const affiliateProfile = affiliateMap[row.affiliateUid] || {};
        return {
          ...row,
          totalCommission: Math.round(Number(row.totalCommission || 0) * 100) / 100,
          instructorLabel:
            row.settlement?.instructorSnapshot?.name ||
            instructorProfile.name ||
            row.settlement?.instructorSnapshot?.slug ||
            shortId(row.instructorUid),
          instructorEmail: instructorProfile.email || "",
          affiliateLabel:
            row.settlement?.affiliateSnapshot?.name ||
            affiliateProfile.name ||
            row.settlement?.affiliateSnapshot?.code ||
            affiliateProfile.referralCode ||
            shortId(row.affiliateUid),
          affiliateEmail: affiliateProfile.email || "",
          referralCode:
            row.settlement?.affiliateSnapshot?.code ||
            affiliateProfile.referralCode ||
            row.orders[0]?.referralCode ||
            "",
          slipUrl: row.settlement?.slipUrl || "",
          slipType: row.settlement?.slipType || "",
          adminNote: row.settlement?.adminNote || "",
          affiliateNote: row.settlement?.affiliateNote || "",
          updatedAt:
            row.settlement?.updatedAt ||
            row.settlement?.submittedAt ||
            row.orders[0]?.approvedAt ||
            row.orders[0]?.createdAt ||
            null,
        };
      })
      .sort((a, b) => {
        if (a.monthKey !== b.monthKey) return b.monthKey.localeCompare(a.monthKey);
        return Number(b.totalCommission || 0) - Number(a.totalCommission || 0);
      });
  }, [affiliateMap, payouts, settlements, userMap]);

  const monthOptions = useMemo(
    () => Array.from(new Set(rows.map((row) => row.monthKey).filter(Boolean))).sort().reverse(),
    [rows],
  );

  const filteredRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (monthFilter !== "all" && row.monthKey !== monthFilter) return false;
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (!needle) return true;
      return [
        row.instructorLabel,
        row.instructorUid,
        row.instructorEmail,
        row.affiliateLabel,
        row.affiliateUid,
        row.affiliateEmail,
        row.referralCode,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle));
    });
  }, [monthFilter, rows, search, statusFilter]);

  const stats = useMemo(() => {
    return filteredRows.reduce(
      (acc, row) => {
        acc.total += Number(row.totalCommission || 0);
        acc.count += 1;
        acc[row.status] = (acc[row.status] || 0) + 1;
        return acc;
      },
      { total: 0, count: 0, ready: 0, submitted: 0, confirmed: 0, disputed: 0 },
    );
  }, [filteredRows]);

  async function saveReview() {
    if (!reviewState.row?.settlement || reviewState.busy) return;
    setReviewState((prev) => ({ ...prev, busy: true }));
    try {
      const reviewerId = getAuth().currentUser?.uid || null;
      const row = reviewState.row;
      const batch = writeBatch(db);
      const settlementRef = doc(db, "affiliate_settlements", row.settlement.id || row.id);
      batch.update(settlementRef, {
        status: reviewState.status,
        adminNote: reviewState.note.trim() || null,
        adminReviewedAt: serverTimestamp(),
        adminReviewedBy: reviewerId,
        updatedAt: serverTimestamp(),
      });
      (row.payoutIds || []).forEach((payoutId) => {
        batch.update(doc(db, "payouts", payoutId), {
          settlementStatus: reviewState.status,
          updatedAt: serverTimestamp(),
        });
      });
      await batch.commit();
      await Promise.all([
        notifyUserDoc(["users", row.instructorUid, "notifications"], {
          userId: row.instructorUid,
          recipientUid: row.instructorUid,
          status: reviewState.status,
          type: "affiliate_settlement_admin",
          settlementId: row.settlement.id || row.id,
          message: `Admin updated the ${monthLabel(row.monthKey)} affiliate settlement to ${reviewState.status}.`,
        }),
        notifyUserDoc(["referral_users", row.affiliateUid, "notifications"], {
          userId: row.affiliateUid,
          recipientUid: row.affiliateUid,
          status: reviewState.status,
          type: "affiliate_settlement_admin",
          settlementId: row.settlement.id || row.id,
          message: `Admin updated the ${monthLabel(row.monthKey)} settlement to ${reviewState.status}.`,
        }),
      ]);
      setReviewState({
        open: false,
        row: null,
        status: "submitted",
        note: "",
        busy: false,
      });
    } catch (error) {
      console.error("admin settlement review failed", error);
      alert("Failed to update settlement.");
      setReviewState((prev) => ({ ...prev, busy: false }));
    }
  }

  return (
    <div className="space-y-6">
      <div className="rounded-3xl border bg-white p-5 shadow-sm md:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-black tracking-tight md:text-3xl">
              Affiliate Settlements
            </h1>
            <p className="mt-2 max-w-3xl text-sm text-gray-600 md:text-base">
              Monitor monthly affiliate dues across instructors, submitted payout slips,
              and disputed confirmations from one place.
            </p>
          </div>
          <div className="rounded-2xl bg-blue-50 px-4 py-3 text-sm text-blue-800">
            Visible groups: <span className="font-semibold">{filteredRows.length}</span>
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-6">
        <div className="rounded-3xl border bg-white p-5 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Total Due</div>
          <div className="mt-2 text-2xl font-black">{money(stats.total)}</div>
        </div>
        <div className="rounded-3xl border bg-white p-5 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Ready</div>
          <div className="mt-2 text-2xl font-black">{stats.ready}</div>
        </div>
        <div className="rounded-3xl border bg-white p-5 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Submitted</div>
          <div className="mt-2 text-2xl font-black">{stats.submitted}</div>
        </div>
        <div className="rounded-3xl border bg-white p-5 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Confirmed</div>
          <div className="mt-2 text-2xl font-black">{stats.confirmed}</div>
        </div>
        <div className="rounded-3xl border bg-white p-5 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Disputed</div>
          <div className="mt-2 text-2xl font-black">{stats.disputed}</div>
        </div>
        <div className="rounded-3xl border bg-white p-5 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Unassigned Pool</div>
          <div className="mt-2 text-2xl font-black">{money(unassignedPool)}</div>
        </div>
      </div>

      <div className="rounded-3xl border bg-white p-5 shadow-sm">
        <div className="grid gap-3 md:grid-cols-[1.4fr,0.9fr,0.8fr]">
          <label className="flex items-center gap-2 rounded-2xl border px-4 py-3 text-sm text-gray-600">
            <FiSearch className="shrink-0 text-gray-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search instructor, affiliate, UID, or code"
              className="w-full bg-transparent outline-none"
            />
          </label>
          <select
            className="rounded-2xl border px-4 py-3 text-sm outline-none"
            value={monthFilter}
            onChange={(event) => setMonthFilter(event.target.value)}
          >
            <option value="all">All months</option>
            {monthOptions.map((monthKey) => (
              <option key={monthKey} value={monthKey}>
                {monthLabel(monthKey)}
              </option>
            ))}
          </select>
          <select
            className="rounded-2xl border px-4 py-3 text-sm outline-none"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <option value="all">All statuses</option>
            <option value="ready">Ready</option>
            <option value="submitted">Submitted</option>
            <option value="confirmed">Confirmed</option>
            <option value="disputed">Disputed</option>
          </select>
        </div>
      </div>

      <div className="space-y-4">
        {filteredRows.length === 0 ? (
          <div className="rounded-3xl border bg-white p-10 text-center text-gray-500 shadow-sm">
            No settlement groups match these filters.
          </div>
        ) : (
          filteredRows.map((row) => (
            <div key={`${row.monthKey}_${row.instructorUid}_${row.affiliateUid}`} className="rounded-3xl border bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-xl font-black">{row.instructorLabel}</h2>
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusTone(row.status)}`}>
                      {row.status}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                    <span className="inline-flex items-center gap-2"><FiCalendar /> {monthLabel(row.monthKey)}</span>
                    <span className="inline-flex items-center gap-2"><FiDollarSign /> {money(row.totalCommission)}</span>
                    <span className="inline-flex items-center gap-2"><FiFile /> {row.orderCount} sales</span>
                    <span className="inline-flex items-center gap-2"><FiUsers /> {row.affiliateLabel}</span>
                  </div>

                  <div className="text-xs text-gray-500">
                    Instructor: {row.instructorUid}
                    {row.instructorEmail ? ` • ${row.instructorEmail}` : ""}
                  </div>
                  <div className="text-xs text-gray-500">
                    Affiliate: {row.affiliateUid}
                    {row.affiliateEmail ? ` • ${row.affiliateEmail}` : ""}
                    {row.referralCode ? ` • Code: ${row.referralCode}` : ""}
                  </div>

                  {row.adminNote ? (
                    <div className="rounded-2xl bg-slate-100 px-4 py-3 text-sm text-slate-700">
                      Admin note: {row.adminNote}
                    </div>
                  ) : null}
                  {row.affiliateNote ? (
                    <div className="rounded-2xl bg-rose-50 px-4 py-3 text-sm text-rose-700">
                      Affiliate note: {row.affiliateNote}
                    </div>
                  ) : null}

                  <div className="text-xs text-gray-500">
                    Updated: {formatDate(row.updatedAt)}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="rounded-2xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
                    onClick={() => setOrdersViewer({ open: true, row })}
                  >
                    View sales
                  </button>
                  {row.slipUrl ? (
                    <button
                      type="button"
                      className="rounded-2xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
                      onClick={() =>
                        setSlipViewer({
                          open: true,
                          url: row.slipUrl,
                          kind: row.slipType || slipKindOf(row.slipUrl),
                          title: `${row.instructorLabel} → ${row.affiliateLabel}`,
                        })
                      }
                    >
                      <FiEye className="inline-block" /> Open slip
                    </button>
                  ) : null}
                  {row.settlement ? (
                    <button
                      type="button"
                      className="rounded-2xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                      onClick={() =>
                        setReviewState({
                          open: true,
                          row,
                          status: row.status || "submitted",
                          note: row.adminNote || "",
                          busy: false,
                        })
                      }
                    >
                      Review
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <Modal open={ordersViewer.open} onClose={() => setOrdersViewer({ open: false, row: null })}>
        <div className="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h3 className="text-xl font-black">Sales details</h3>
              <p className="mt-1 text-sm text-gray-600">
                {ordersViewer.row?.instructorLabel || "Instructor"} → {ordersViewer.row?.affiliateLabel || "Affiliate"}
              </p>
            </div>
            <button
              type="button"
              className="rounded-2xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
              onClick={() =>
                navigator.clipboard?.writeText(String(ordersViewer.row?.orderIds?.join(", ") || ""))
              }
            >
              <FiCopy className="inline-block" /> Copy IDs
            </button>
          </div>
          <div className="space-y-3">
            {(ordersViewer.row?.orders || []).map((order) => (
              <div key={order.id} className="rounded-2xl border p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="font-semibold">{titleOf(order)}</div>
                    <div className="mt-1 text-sm text-gray-500">Buyer: {buyerLabel(order)}</div>
                  </div>
                  <div className="text-right text-sm text-gray-600">
                    <div>{money(order.amount || 0)}</div>
                    <div>{formatDate(order.approvedAt || order.completedAt || order.createdAt)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Modal>

      <Modal
        open={reviewState.open}
        onClose={() =>
          !reviewState.busy &&
          setReviewState({ open: false, row: null, status: "submitted", note: "", busy: false })
        }
      >
        <div className="space-y-5">
          <div>
            <h3 className="text-xl font-black">Review settlement</h3>
            <p className="mt-1 text-sm text-gray-600">
              Update the settlement status and save an internal admin note.
            </p>
          </div>

          <div className="rounded-2xl bg-gray-50 p-4 text-sm text-gray-700">
            <div>Instructor: <span className="font-semibold">{reviewState.row?.instructorLabel}</span></div>
            <div className="mt-1">Affiliate: <span className="font-semibold">{reviewState.row?.affiliateLabel}</span></div>
            <div className="mt-1">Month: <span className="font-semibold">{monthLabel(reviewState.row?.monthKey)}</span></div>
            <div className="mt-1">Amount: <span className="font-semibold">{money(reviewState.row?.totalCommission)}</span></div>
          </div>

          <label className="block text-sm font-semibold text-gray-700">
            Status
            <select
              className="mt-2 w-full rounded-2xl border px-4 py-3 text-sm outline-none"
              value={reviewState.status}
              onChange={(event) =>
                setReviewState((prev) => ({ ...prev, status: event.target.value }))
              }
            >
              <option value="submitted">Submitted</option>
              <option value="confirmed">Confirmed</option>
              <option value="disputed">Disputed</option>
            </select>
          </label>

          <label className="block text-sm font-semibold text-gray-700">
            Admin note
            <textarea
              rows={4}
              className="mt-2 w-full rounded-2xl border px-4 py-3 text-sm outline-none focus:border-blue-500"
              placeholder="Optional internal note"
              value={reviewState.note}
              onChange={(event) =>
                setReviewState((prev) => ({ ...prev, note: event.target.value }))
              }
            />
          </label>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="rounded-2xl border px-4 py-2 font-semibold hover:bg-gray-50"
              onClick={() =>
                setReviewState({ open: false, row: null, status: "submitted", note: "", busy: false })
              }
            >
              Cancel
            </button>
            <button
              type="button"
              className="rounded-2xl bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              onClick={saveReview}
              disabled={reviewState.busy}
            >
              {reviewState.busy ? "Saving..." : "Save review"}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={slipViewer.open}
        onClose={() => setSlipViewer({ open: false, url: "", kind: "image", title: "" })}
        max="max-w-5xl"
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h3 className="text-xl font-black">{slipViewer.title || "Payment slip"}</h3>
              <p className="mt-1 text-sm text-gray-600">Review the uploaded payout proof.</p>
            </div>
            <a
              href={slipViewer.url}
              target="_blank"
              rel="noreferrer"
              className="rounded-2xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
            >
              Open in new tab
            </a>
          </div>
          <div className="overflow-hidden rounded-3xl border bg-gray-50">
            {slipViewer.kind === "pdf" ? (
              <iframe title={slipViewer.title || "Slip PDF"} src={slipViewer.url} className="h-[70vh] w-full" />
            ) : (
              <img src={slipViewer.url} alt={slipViewer.title || "Slip"} className="max-h-[70vh] w-full object-contain" />
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}
